import { Field, Label } from '@headlessui/react';
import { GQLOperationalSettingsFragment, GQLProductFragment } from '@theprintbar/graphql';
import {
	Merch,
	cloneDeep,
	cn,
	getSamedayName,
	getSamedayTurnaroundDetails,
	makeMerchSamedayCompatible,
	merchIsSamedayCompatible,
} from '@theprintbar/shared';
import { useCallback, useEffect, useState } from 'react';
import { LightningBolt, OutlineButton, Pill, PrimaryButton, Toggle, useConfirmModal } from '../../..';

interface SamedayBarProps {
	merch?: Merch;
	defaultOn?: boolean;
	product: GQLProductFragment;
	settings: GQLOperationalSettingsFragment;
	updateMerch: (merch: Merch) => void;
	disabled?: boolean;
	greyedOut?: boolean;
}

export function SamedayBar({ merch, settings, defaultOn, product, updateMerch, disabled, greyedOut }: SamedayBarProps) {
	const { colourCompatible, decorationCompatible } = merchIsSamedayCompatible({ product, merch });

	const { samedayName, samedayDescription, samedayTitle, samedayButtonOnTitle, samedayButtonOnDescription } = getSamedayTurnaroundDetails(
		{
			settings,
			merch,
		},
	);

	const sameDayEnabled = !!merch?.sameday || !!(!merch && defaultOn);

	const getInitialSameDayText = () => ({
		title: sameDayEnabled ? samedayButtonOnTitle : samedayTitle,
		description: sameDayEnabled ? samedayButtonOnDescription : samedayDescription,
	});

	const [sameDayText, setSameDayText] = useState(getInitialSameDayText);

	useEffect(() => {
		if (!merch) return;
		setSameDayText({
			title: merch.sameday ? samedayButtonOnTitle : samedayTitle,
			description: merch.sameday ? samedayButtonOnDescription : samedayDescription,
		});
	}, [merch]);

	const onChange = useCallback(async () => {
		if (!merch) return;
		//Check the merch won't be reset if the user toggles the sameday option
		let message = `To make your item ${name} compatible we will update`;
		let result;
		const samedayName = getSamedayName();

		if (!colourCompatible && !decorationCompatible) {
			message +=
				' the product colour and change decorations to Digital Printing where available, otherwise decorations will be disabled.';
		} else if (!colourCompatible) {
			message += ` the product colour to a colour we have in stock for ${samedayName}.`;
		} else if (!decorationCompatible) {
			message += ' decorations to Digital Printing where available, otherwise decorations will be disabled.';
		} else {
			message = '';
		}

		if (message && !sameDayEnabled) {
			setConfirmContent(
				<div className="mb-4">
					<h5 className="mb-2 uppercase">We need to make some changes</h5>
					<p>{message}</p>
				</div>,
			);
			result = await confirm();
			if (!result) return;
		}

		if (sameDayEnabled) {
			updateMerch({ ...cloneDeep(merch), sameday: false });
			setSameDayText({ title: samedayTitle, description: samedayDescription });
		} else {
			const newMerch = { ...makeMerchSamedayCompatible(merch, product), sameday: !merch.sameday };
			updateMerch(newMerch);
			setSameDayText({ title: samedayButtonOnTitle, description: samedayButtonOnDescription });
		}
	}, [merch, disabled, sameDayEnabled, updateMerch]);

	const [confirmContent, setConfirmContent] = useState<React.ReactNode>(null);
	const { confirm, ConfirmModal } = useConfirmModal({
		content: confirmContent,
		className: 'max-w-sm min-h-42',
		actionContainerClassName: 'mt-auto',
		actions: [
			{
				ActionButton: onClick => (
					<PrimaryButton key="submit" onClick={onClick}>
						Confirm
					</PrimaryButton>
				),
				onClick: () => true,
			},
			{
				ActionButton: onClick => (
					<OutlineButton key="cancel" onClick={onClick}>
						Cancel
					</OutlineButton>
				),
				onClick: () => false,
			},
		],
	});
	return !disabled ? (
		<>
			<ConfirmModal />
			<Field>
				<div className={cn('relative border border-blue rounded-3xl  bg-white', greyedOut && 'border-grey')}>
					<div
						className={cn(
							'flex items-center rounded-3xl lg:min-h-20 bg-blue-bright/20',
							greyedOut && 'bg-grey-light text-grey',
						)}
					>
						<Label className={cn('text-xs flex flex-1 items-center px-4 py-6 pb-2 lg:pb-6 lg:py-3 cursor-pointer')}>
							<Pill
								className={cn(
									'absolute -top-3 left-4 lg:static uppercase pl-2 lg:pl-3 py-0 pr-0 lg:mr-4 flex items-center font-belfast text-xs font-bold whitespace-nowrap gap-2 bg-blue-mid',
									greyedOut && 'bg-grey-light text-grey border-grey',
								)}
							>
								{samedayName}

								<Toggle
									name="sameday"
									enabledClassName={'bg-blue-bright'}
									enabled={sameDayEnabled}
									enabledIcon={LightningBolt}
									disabledIcon={LightningBolt}
									disabled={disabled}
									onChange={onChange}
									className="-my-px -mr-px"
									innerLabel
								/>
							</Pill>
							<div>
								<h5 className="uppercase">{sameDayText.title}</h5>
								<p>{sameDayText.description}</p>
							</div>
						</Label>
					</div>
				</div>
			</Field>
		</>
	) : sameDayEnabled ? (
		<Pill className="py-0.5 px-1 flex items-center bg-blue-bright">
			<LightningBolt className="w-3 h-3 lg:w-4 lg:h-4" />
			{samedayName}
		</Pill>
	) : null;
}
